<template lang="pug">
  .graph-component(ref="graphWrap", :class="{grabbing}")
    transition(name="fade")
      .graph-tip(v-if="scrollingTip")
        label.tip Use Alt + scroll to zoom
    .graph-wrap(ref="graph")
      .graph
        .nodes
          .links
            svg
              path.link(
              :class="{active: node.isActive}",
              @mouseover="toggleHighlight(node, true)",
              @mouseleave="toggleHighlight(node, false)",
              v-if="node.type !== 'current'",
              v-for="node, i in nodes", :key="i",
              :d="createD(node)")
              defs
                marker#arrow(viewBox="0 -2 10 4", refX="17", refY="0", markerUnits="strokeWidth",
                markerWidth="15", markerHeight="6", xoverflow="visible", orient="auto")
                  path(d="M0,-2 L0,2 L10,0 z", :fill="colors.defaultColorMiddle")
          .node(
          v-for="node, i in nodes", :key="i",
          @mouseover="toggleHighlight(node, true)",
          @mouseleave="toggleHighlight(node, false)",
          :style="setCirclePosition(node)", :class="{active: node.isActive}")
            router-link.circle(
            :class="setClass(node)",
            :to="{name: 'RecordDetailsPage', params: {id: node.id}}")
            label.name {{node.name}}
</template>

<script>
import { mapGetters, mapState, } from 'vuex'
import * as colors from '../../constants/colors'
import panzoom from 'panzoom'
import debounce from 'lodash/debounce'

export default {
  data () {
    return {
      colors,
      scrollingTip: false,
      grabbing: false,
      parents: [],
      children: [],
      nodes: [],
      step: 0.2,
      rowAmount: 8,
      ellipseRadius: 250,
      offsetRadius: 20,
      angleCoefficient: 2.5,
      offsetYCoefficient: 50,
      initialZoom: 1.1,
    }
  },
  computed: {
    ...mapState('record', [
      'directParent',
      'ultimateParent',
      'record',
      'loading',
    ]),
    ...mapGetters('record', [ 'companyName', 'directParentName', 'ultimateParentName', ]),
  },
  mounted () {
    this.init()
  },
  methods: {
    createD ({ target, source, }) {
      return `M${target.x},${target.y} L${source.x},${source.y},`
    },
    setCirclePosition ({ x, y, }) {
      return {
        left: x + 'px',
        top: y + 'px',
      }
    },
    setClass ({ type, relationship, isActive, }) {
      return !isActive ? `${relationship}${type}` : 'active'
    },
    setLinks (nodes) {
      const current = this.createNode(this.record, 'current')
      return nodes.map((n) => (
        n.type === 'child' ? {
          ...n,
          target: n,
          source: current,
        } : {
          ...n,
          target: current,
          source: n,
        }))
    },
    setParents () {
      if (this.ultimateParent) this.parents.push(this.createNode(this.ultimateParent, 'parent', 'ultimate'))
      if (this.directParent) this.parents.push(this.createNode(this.directParent, 'parent', 'direct'))
    },
    setChildren () {
      if (this.record.children && this.record.children.length !== 0) {
        this.children = this.record.children.map((child) => this.createNode(child, 'child'))
      }
    },
    setNodes () {
      this.nodes = [ this.createNode(this.record, 'current'), ...this.setLinks(this.calculateCoordinates(this.parents)), ...this.setLinks(this.calculateCoordinates(this.children)), ]
    },
    createNode ({ entity: { lei, id, legalName: { name, }, }, }, type, relationship = '') {
      return {
        lei,
        id,
        name,
        relationship,
        type,
        x: 0,
        y: 0,
        isActive: false,
      }
    },
    calculateCoordinates (nodes) {
      const getPoint = (r, angle) => ({
        x: r * Math.sin(angle),
        y: r * Math.cos(angle) / this.angleCoefficient,
      })

      const getAngle = (nodes, index, type) => {
        const definer = {
          parent: Math.PI,
          child: 0,
        }
        const offsetAngle = definer[type]
        const total = nodes.length
        return offsetAngle + this.step * index - (total - 1) / 2 * this.step
      }

      const calcOffset = (index) => index * this.offsetYCoefficient

      return nodes.map((n, i) => {
        const gi = Math.floor(i / this.rowAmount)
        const { x, y, } = getPoint(
          this.ellipseRadius + gi * this.offsetRadius,
          getAngle(nodes.slice(gi * this.rowAmount, (gi + 1) * this.rowAmount), i % this.rowAmount, n.type)
        )

        return {
          ...n,
          x,
          y: y + calcOffset(gi),
        }
      })
    },
    setChildrenForTest () {
      this.children = new Array(26).fill(0).map((item, index) => (this.createNode({
        id: '097900BHML0000101',
        lei: '097900BHML000012',
        legalname: { name: '097900BHML0000101', },
      }, 'child')))
    },
    setInitialValues () {
      const total = this.children.length
      const range = (min, max) => total >= min && total < max
      const setValues =
        (rowAmount, step, initialZoom, offsetYCoefficient, angleCoefficient, offsetRadius, ellipseRadius) => {
          this.rowAmount = rowAmount
          // this.rowAmount =(total < 25 ? 8 : Math.ceil(Math.ceil(6 + Math.pow(total, 1 / 1.41) / 3) / 2) * 2)
          // console.log('rowAmount', rowAmount, this.rowAmount)
          this.ellipseRadius = ellipseRadius
          this.offsetRadius = offsetRadius
          this.angleCoefficient = angleCoefficient
          this.offsetYCoefficient = offsetYCoefficient // 50 + Math.sqrt(total) //
          // console.log('offsetYCoefficient', offsetYCoefficient, this.offsetYCoefficient)
          this.step = step // 0.69 - Math.pow(total, 1 / 9.9) / 3 //
          // console.log('step', step, this.step)
          this.initialZoom = initialZoom // 120 / (total + 90) + 0.125 //
        }
      if (range(0, 8)) setValues(8, 0.3, 1.35, 50, 2, 20, 250)
      if (range(8, 25)) setValues(8, 0.275, 1.15, 50, 2.5, 20, 250)
      if (range(25, 40)) setValues(10, 0.25, 1.1, 50, 3, 25, 300)
      if (range(40, 70)) setValues(12, 0.2, 0.95, 55, 4, 20, 350)
      if (range(70, 100)) setValues(14, 0.15, 0.8, 60, 5, 15, 500)
      if (range(100, 200)) setValues(18, 0.125, 0.6, 65, 7, 10, 600)
      if (range(200, 400)) setValues(26, 0.1, 0.425, 70, 9, 5, 1000)
      if (range(400, 750)) setValues(40, 0.05, 0.35, 75, 10, 5, 1500)
    },
    toggleHighlight (node, value) {
      node.isActive = value
    },
    hideTipDebounce: debounce(function () {
      this.scrollingTip = false
    }, 1000),
    addListeners () {
      document.body.addEventListener('panstart', () => {
        this.grabbing = true
      }, true)
      document.body.addEventListener('panend', () => {
        this.grabbing = false
      }, true)
    },
    init () {
      this.addListeners()
      this.setParents()
      this.setChildren()
      this.setInitialValues()
      this.setNodes()
      const width = this.$refs.graphWrap.offsetWidth
      panzoom(this.$refs.graph, {
        smoothScroll: false,
        maxZoom: 3,
        minZoom: 0.1,
        beforeWheel: (e) => {
          this.scrollingTip = !e.altKey
          this.hideTipDebounce()
          return !e.altKey
        },
      }).zoomAbs(
        width / 2,
        0,
        this.initialZoom
      )
    },
  },
}
</script>

<style lang="stylus">
  @import "../../assets/styles/variables.styl"

  .graph-component
    position relative
    overflow hidden
    width 100%
    height 660px
    border 1px solid $default-color-light-2
    cursor grab
    &.grabbing
      cursor grabbing
      .graph svg .path .link.active, .graph .nodes .node.active .circle
        cursor grabbing

    .graph-wrap
      position relative
      width 100%
      height 100%

    .graph-tip
      display flex
      align-items center
      justify-content center
      position absolute
      top 0
      left 0
      width 100%
      height 100%
      background-color $default-color-bold-opacity
      z-index 2
      padding 25px
      .tip
        font-size 150%
        color $white-color
        font-weight 500

    .graph
      width 100%
      height 100%
      position relative
      transform translate(50%, 35%)
      svg
        overflow unset
      .link
        stroke $default-color-middle
        stroke-width 0.05rem
        &.active
          stroke $warning-color
          cursor pointer
        &.child
          stroke $default-color-light-1
        &.current
          stroke $success-color
        &.parent
          stroke $primary-color-light-1
        &.ultimateparent
          stroke $primary-color-light-1
        &.directparent
          stroke $primary-color-light-1

      .node
        position absolute
        &.active
          .name
            display inline
          .circle
            cursor pointer
            background-color $warning-color-light-1
            border-color $warning-color
        .circle
          background $primary-color
          border-radius 50%
          border 1px solid $default-color
          width 25px
          height 25px
          position absolute
          z-index 0
          transform translate(-50%, -50%)
          &.child
            background-color $default-color-light-1
          &.current
            background-color $success-color
          &.parent
            background-color $primary-color-light-1
          &.ultimateparent
            background-color $primary-color-light-1
          &.directparent
            background-color $primary-color-light-1

        .name
          text-shadow 1px 1px 2px $white-color, 0 0 0.5em $white-color, 0 0 1em $white-color, 0 0 5px $white-color
          display none
          position absolute
          z-index 20
          font-weight 600
          top -1.75rem
          transform translateX(-50%)
          white-space nowrap
          font-size 70%
          color $default-color-bold

  .fade-enter-active, .fade-leave-active
    transition opacity .5s

  .fade-enter, .fade-leave-to
    opacity 0

  @media screen and (max-width: $laptop)
    .graph-component
      display none
</style>




// WEBPACK FOOTER //
// src/components/record-details/Graph.vue